import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TierBadge, StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Coins, Ban, CheckCircle2, UserCheck, X, SlidersHorizontal, Users } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { GrantPointsDialog } from "@/components/admin/GrantPointsDialog";
import { Pager } from "@/components/admin/Pager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebounced } from "@/hooks/useDebounced";
import { fmtNum, plural } from "@/lib/format";

const PAGE_SIZE = 25;

export default function AdminUsers() {
  usePageTitle("Clientes · Gestión de clientes");
  // Pestaña, filtro, búsqueda y página en la URL: Inicio enlaza a ?tab=pending y el estado sobrevive a una recarga.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const status = params.get("estado") || "";
  const page = Number(params.get("page") || 1);
  const [qInput, setQInput] = useState(params.get("q") || "");
  const q = useDebounced(qInput, 300);
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v && !(k === "tab" && v === "all")) next.set(k, v); else next.delete(k);
    if (k !== "page") next.delete("page");
    setParams(next, { replace: true });
  };
  useEffect(() => { if (q !== (params.get("q") || "")) setParam("q", q); }, [q]); // eslint-disable-line

  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [granting, setGranting] = useState<any>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [statusTarget, setStatusTarget] = useState<any>(null);
  const [statusValue, setStatusValue] = useState("active");
  const [statusReason, setStatusReason] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const load = () => {
    setFailed(false);
    return api.get("/admin/users/search", { params: { q, tab, status: status || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); setCheckedIds([]); }, [q, tab, status, page]); // eslint-disable-line

  // Inhabilitar retira todo el saldo: siempre pasa por el diálogo con la advertencia.
  const openStatus = (u: any, s?: string) => {
    setStatusTarget(u); setStatusValue(s || u.status || "active"); setStatusReason(u.inactive_reason || "");
  };

  const saveStatus = async () => {
    if (!statusTarget) return;
    setStatusSaving(true);
    try {
      await api.post(`/admin/users/${statusTarget.id}/status`, { status: statusValue, reason: statusValue === "inactive" ? (statusReason || null) : null });
      const label: Record<string, string> = { active: "al día", overdue: "no al día", inactive: "inhabilitado" };
      toast.success(`${statusTarget.name} quedó ${label[statusValue]}.`);
      setStatusTarget(null); load();
    } catch (err: any) { toast.error(apiError(err)); }
    finally { setStatusSaving(false); }
  };

  const decideApproval = async (u: any, decision: string, reason?: string) => {
    try {
      await api.post(`/admin/users/${u.id}/approval`, { decision, reason: reason || null });
      toast.success(decision === "approved" ? `Cuenta de ${u.name} aprobada. Recibirá una notificación.` : `Registro de ${u.name} rechazado.`);
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
  };

  const items: any[] = data?.items || [];
  const counts = data?.counts || { all: 0, pending: 0, approved: 0, rejected: 0 };
  const selectable = items.filter((u) => (u.approval_status || "approved") === "pending");
  const allChecked = selectable.length > 0 && checkedIds.length === selectable.length;
  const toggleAll = () => setCheckedIds(allChecked ? [] : selectable.map((u) => u.id));
  const toggleOne = (id: string) => setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const bulkDecide = async (decision: string, reason?: string) => {
    if (checkedIds.length === 0) return;
    setBulkApproving(true);
    try {
      const { data: r } = await api.post("/admin/users/approval-bulk", { user_ids: checkedIds, decision, reason: reason || null });
      toast.success(`${plural(r.updated, "registro", "registros")} ${decision === "approved" ? "aprobados" : "rechazados"}`);
      setCheckedIds([]);
      load();
    } catch (err: any) { toast.error(apiError(err)); throw err; }
    finally { setBulkApproving(false); }
  };

  const confirmReject = async () => {
    const reason = rejectReason.trim();
    try {
      if (rejectTarget === "bulk") await bulkDecide("rejected", reason);
      else await decideApproval(rejectTarget, "rejected", reason);
      setRejectTarget(null); setRejectReason("");
    } catch { /* el aviso ya se mostró; el diálogo queda abierto */ }
  };

  const actions = (u: any) => {
    const approval = u.approval_status || "approved";
    if (approval === "pending")
      return (
        <>
          <Button data-testid="approve-user-button" size="sm" onClick={() => decideApproval(u, "approved").catch(() => {})} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-9">
            <UserCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Aprobar
          </Button>
          <Button data-testid="reject-user-button" size="sm" variant="outline" onClick={() => setRejectTarget(u)} className="border-red-500/30 text-red-700 hover:bg-red-500/10 rounded-full h-9">
            <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Rechazar
          </Button>
        </>
      );
    if (approval === "rejected")
      return (
        <Button data-testid="reopen-user-button" size="sm" onClick={() => decideApproval(u, "approved").catch(() => {})} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-9">
          <UserCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Aprobar
        </Button>
      );
    return (
      <>
        <Button data-testid="adjust-points-button" size="sm" variant="outline" onClick={() => setGranting(u)} className="border-gold/40 text-gold-ink hover:bg-gold/10 h-9">
          <Coins className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Puntos
        </Button>
        <Button data-testid="edit-status-button" size="sm" variant="outline" onClick={() => openStatus(u)} className="border-gold/40 text-gold-ink hover:bg-gold/10 h-9">
          <SlidersHorizontal className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Estado
        </Button>
        {u.status === "inactive" ? (
          <Button data-testid="enable-user-button" size="sm" variant="outline" onClick={() => openStatus(u, "active")} className="border-emerald-500/30 text-emerald-700 hover:bg-emerald-500/10 h-9" title="Reactivar acceso" aria-label={`Reactivar a ${u.name}`}>
            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
        ) : (
          <Button data-testid="disable-user-button" size="sm" variant="outline" onClick={() => openStatus(u, "inactive")} className="border-red-500/30 text-red-700 hover:bg-red-500/10 h-9" title="Inhabilitar acceso" aria-label={`Inhabilitar a ${u.name}`}>
            <Ban className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
        )}
      </>
    );
  };

  if (!data && !failed) return <PageLoader />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input
            data-testid="user-search-input"
            aria-label="Buscar clientes"
            value={qInput}
            onChange={(e: any) => setQInput(e.target.value)}
            placeholder="Buscar por nombre, cédula o correo"
            className="pl-9 bg-obsidian-card border-slate-200 text-slate-900 h-11"
          />
        </div>
        <div className="w-full sm:w-56">
          <Label htmlFor="status-filter" className="sr-only">Filtrar por estado</Label>
          <Select value={status || "any"} onValueChange={(v: string) => setParam("estado", v === "any" ? "" : v)}>
            <SelectTrigger id="status-filter" data-testid="user-status-filter" className="h-11 bg-obsidian-card border-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
              <SelectItem value="any">Cualquier estado</SelectItem>
              <SelectItem value="active">Al día</SelectItem>
              <SelectItem value="overdue">No al día</SelectItem>
              <SelectItem value="inactive">Inhabilitados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: `Todos (${fmtNum(counts.all)})` },
          { key: "pending", label: `Por aprobar (${fmtNum(counts.pending)})` },
          { key: "approved", label: `Aprobados (${fmtNum(counts.approved)})` },
          { key: "rejected", label: `Rechazados (${fmtNum(counts.rejected)})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`user-tab-${t.key}`}
            onClick={() => setParam("tab", t.key)}
            aria-pressed={tab === t.key}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
              tab === t.key ? "gold-gradient-bg text-white font-semibold border-transparent" : "border-slate-200 text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "pending" && checkedIds.length > 0 && (
        <div data-testid="bulk-approval-bar" className="card-obsidian p-4 flex flex-wrap items-center justify-between gap-3 border-gold/30">
          <span className="text-sm text-slate-700">{plural(checkedIds.length, "registro seleccionado", "registros seleccionados")}</span>
          <div className="flex items-center gap-2">
            <Button data-testid="bulk-approve-button" size="sm" onClick={() => bulkDecide("approved").catch(() => {})} disabled={bulkApproving} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
              {bulkApproving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : <><UserCheck className="w-4 h-4 mr-1" aria-hidden="true" /> Aprobar seleccionados</>}
            </Button>
            <Button data-testid="bulk-reject-button" size="sm" variant="outline" onClick={() => setRejectTarget("bulk")} disabled={bulkApproving} className="border-red-500/30 text-red-700 hover:bg-red-500/10 rounded-full">
              <X className="w-4 h-4 mr-1" aria-hidden="true" /> Rechazar seleccionados
            </Button>
          </div>
        </div>
      )}

      {failed ? (
        <LoadError what="los clientes" onRetry={load} />
      ) : items.length === 0 ? (
        <EmptyState icon={Users}>
          <span data-testid="users-empty">
            {q ? <>No encontramos clientes para “{q}”{tab !== "all" || status ? " con estos filtros" : ""}. Prueba con la cédula o el correo.</> : "No hay clientes con estos filtros."}
          </span>
        </EmptyState>
      ) : (
        <div className="card-obsidian overflow-hidden">
          {/* Escritorio: tabla. Móvil: tarjetas, para que el saldo y las acciones no queden fuera de la pantalla. */}
          <div className="hidden md:block overflow-x-auto" tabIndex={0} role="region" aria-label="Tabla de clientes">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  {tab === "pending" && (
                    <th scope="col" className="px-5 py-3 w-10">
                      <Checkbox data-testid="select-all-checkbox" aria-label="Seleccionar todos los de esta página" checked={allChecked} onCheckedChange={toggleAll} />
                    </th>
                  )}
                  <th scope="col" className="text-left font-medium px-5 py-3 min-w-[14rem]">Cliente</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Cédula</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Nivel</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Puntos</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Registro</th>
                  <th scope="col" className="text-left font-medium px-5 py-3">Estado</th>
                  <th scope="col" className="text-right font-medium px-5 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} data-testid="admin-user-row" className="border-b border-slate-200 hover:bg-slate-50">
                    {tab === "pending" && (
                      <td className="px-5 py-3">
                        <Checkbox data-testid={`row-checkbox-${u.id}`} aria-label={`Seleccionar a ${u.name}`} checked={checkedIds.includes(u.id)} onCheckedChange={() => toggleOne(u.id)} />
                      </td>
                    )}
                    <td className="px-5 py-3">
                      <Link to={`/admin/usuarios/${u.id}`} data-testid="user-detail-link" className="text-slate-900 font-medium hover:underline">{u.name}</Link>
                      <p className="text-xs text-slate-600">{u.email}</p>
                      {u.approval_status === "rejected" && u.approval_reason && <p className="text-xs text-red-700 mt-0.5">Motivo: {u.approval_reason}</p>}
                    </td>
                    <td className="px-5 py-3 font-mono text-slate-700">{u.cedula || "—"}</td>
                    <td className="px-5 py-3"><TierBadge tier={u.tier} /></td>
                    <td className="px-5 py-3 text-right font-mono text-slate-900">{fmtNum(u.points)}</td>
                    <td className="px-5 py-3"><StatusBadge status={u.approval_status || "approved"} /></td>
                    <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                    <td className="px-5 py-3"><div className="flex items-center justify-end gap-2">{actions(u)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="md:hidden divide-y divide-slate-200">
            {items.map((u) => (
              <li key={u.id} className="p-4 space-y-2" data-testid="admin-user-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/admin/usuarios/${u.id}`} className="font-medium text-slate-900 hover:underline">{u.name}</Link>
                    <p className="text-xs text-slate-600 break-all">{u.email} · {u.cedula || "sin cédula"}</p>
                  </div>
                  {tab === "pending" && <Checkbox aria-label={`Seleccionar a ${u.name}`} checked={checkedIds.includes(u.id)} onCheckedChange={() => toggleOne(u.id)} />}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <TierBadge tier={u.tier} />
                  <span className="font-mono text-sm">{fmtNum(u.points)} pts</span>
                  <StatusBadge status={u.approval_status || "approved"} />
                  <StatusBadge status={u.status} />
                </div>
                <div className="flex flex-wrap gap-2">{actions(u)}</div>
              </li>
            ))}
          </ul>
          <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={(p) => setParam("page", String(p))} />
        </div>
      )}

      <GrantPointsDialog user={granting} onClose={() => setGranting(null)} onDone={load} />

      <Dialog open={!!rejectTarget} onOpenChange={(o: boolean) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Rechazar {rejectTarget === "bulk" ? plural(checkedIds.length, "registro", "registros") : `el registro de ${rejectTarget?.name || ""}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">La persona no podrá canjear ni usar el programa y recibirá una notificación. Podrás aprobarla más tarde si cambias de opinión.</p>
            <Label htmlFor="reject-reason" className="text-slate-700 text-sm">Motivo del rechazo (visible para el cliente)</Label>
            <Textarea id="reject-reason" data-testid="reject-reason-input" rows={4} value={rejectReason} onChange={(e: any) => setRejectReason(e.target.value)} placeholder="Ej: El documento no coincide con nuestros registros. Acércate a un asesor para validar tus datos." className="bg-obsidian border-slate-200 text-slate-900" />
            <p className="text-xs text-slate-600">Opcional, pero recomendado para que el cliente sepa cómo continuar.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(""); }} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="confirm-reject-button" onClick={confirmReject} className="bg-red-700 hover:bg-red-800 text-white rounded-full">
              <X className="w-4 h-4 mr-1" aria-hidden="true" /> Confirmar rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!statusTarget} onOpenChange={(o: boolean) => !o && setStatusTarget(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle className="font-serif text-xl">Estado de {statusTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="status-select" className="text-slate-700 text-sm">Estado del cliente</Label>
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger id="status-select" data-testid="status-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                  <SelectItem value="active">Al día — acceso completo</SelectItem>
                  <SelectItem value="overdue">No al día — puede entrar, con un aviso de pagos pendientes</SelectItem>
                  <SelectItem value="inactive">Inhabilitado — sin acceso y sin puntos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {statusValue === "inactive" && (
              <>
                <div data-testid="inactive-warning" className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-800 space-y-1">
                  <p>El cliente no podrá iniciar sesión ni usar la plataforma hasta que lo reactives.</p>
                  {statusTarget?.status !== "inactive" && (statusTarget?.points || 0) > 0 && (
                    <p className="font-semibold">Se retirarán sus {fmtNum(statusTarget.points)} puntos. Reactivar la cuenta no los devuelve.</p>
                  )}
                  <p>Recibirá un correo y una notificación avisándole.</p>
                </div>
                <div>
                  <Label htmlFor="status-reason" className="text-slate-700 text-sm">Motivo de la inhabilitación (visible para el cliente)</Label>
                  <Textarea
                    id="status-reason"
                    data-testid="status-reason-input"
                    rows={3}
                    value={statusReason}
                    onChange={(e: any) => setStatusReason(e.target.value)}
                    placeholder="Ej: Tienes pagos pendientes. Regulariza tu situación para reactivar tu membresía."
                    className="mt-1.5 bg-obsidian border-slate-200 text-slate-900"
                  />
                  <p className="text-xs text-slate-600 mt-1.5">Opcional, pero recomendado para que el cliente sepa cómo reactivar su cuenta.</p>
                </div>
              </>
            )}
            {statusValue !== "inactive" && statusTarget?.status === "inactive" && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-800">
                El cliente podrá volver a ingresar y recibirá un correo de reactivación. Los puntos retirados no se reponen solos: puedes devolverlos con “Puntos”.
              </div>
            )}
            {statusValue === "overdue" && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-800">
                El cliente podrá ingresar y verá un aviso de que no está al día con sus pagos.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusTarget(null)} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
            <Button data-testid="save-status-button" onClick={saveStatus} disabled={statusSaving} className={`${statusValue === "inactive" ? "bg-red-700 hover:bg-red-800" : "gold-gradient-bg"} text-white font-semibold rounded-full`}>
              {statusSaving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" />
                : statusValue === "inactive" && statusTarget?.status !== "inactive" && (statusTarget?.points || 0) > 0
                  ? `Inhabilitar y retirar ${fmtNum(statusTarget.points)} pts`
                  : "Guardar estado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
